import React from "react";
import { MdAdd, MdRemove, MdPerson } from "react-icons/md";

interface AssignmentItemCardProps {
  id: number;
  title: string;
  subtitle?: string;
  imageUrl?: string | null;
  icon?: React.ReactNode;
  tags?: Array<{
    label: string;
    color: string;
  }>;
  isAssigned: boolean;
  onToggle: (id: number) => void;
  isToggling: boolean;
  onClick?: (id: number) => void;
  showImage?: boolean;
}

const AssignmentItemCard: React.FC<AssignmentItemCardProps> = ({
  id,
  title,
  subtitle,
  imageUrl,
  icon,
  tags = [],
  isAssigned,
  onToggle,
  isToggling,
  onClick,
  showImage = true,
}) => {
  const handleCardClick = () => {
    if (onClick) {
      onClick(id);
    }
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(id);
  };

  return (
    <div
      className={`flex items-center justify-between rounded-lg border p-3 transition-colors ${
        isAssigned
          ? "border-biafit-primary bg-biafit-primary bg-opacity-5"
          : "border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:bg-navy-700 dark:hover:bg-navy-800"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center">
        {showImage && (
          <div className="mr-3 h-10 w-10 flex-shrink-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={title}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : icon ? (
              icon
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-biafit-primary text-white">
                <MdPerson className="h-5 w-5" />
              </div>
            )}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h4
            className={`mb-1 truncate text-sm font-medium ${
              onClick
                ? "w-fit cursor-pointer text-navy-700 hover:text-biafit-primary dark:text-white dark:hover:text-biafit-primary"
                : "text-navy-700 dark:text-white"
            }`}
            onClick={handleCardClick}
          >
            {title}
          </h4>
          {subtitle && (
            <p className="mb-1 truncate text-xs text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${tag.color}`}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <button
        onClick={handleToggleClick}
        disabled={isToggling}
        className={`ml-2 p-2 disabled:opacity-50 ${
          isAssigned
            ? "text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
            : "text-biafit-primary hover:text-biafit-secondary dark:text-biafit-primary dark:hover:text-biafit-secondary"
        }`}
        title={isAssigned ? "Remove" : "Add"}
      >
        {isAssigned ? (
          <MdRemove className="h-5 w-5" />
        ) : (
          <MdAdd className="h-5 w-5" />
        )}
      </button>
    </div>
  );
};

export default AssignmentItemCard;
